/**
 * @file inward-delivery-files.tsx
 * @module app/modules/inward/components/inward-delivery-files
 * @description Warehouse photos and Finance documents of an Inward GRN, shown in the GRN
 *   section of the PO details page (old UProcure and Procurement (New)). Each file opens in
 *   the same pop-up viewer as on the Inward GRN screen (any file type).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 2.1.2
 */

import { useState } from "react";
import { formatDateTime } from "../utils/inward-timeline";
import InwardImageViewer, {
  fileExtension,
  fileKind,
} from "./inward-image-viewer";
import "../styles/inward.css";
import ImageWithFallback from "../../_common/components/image-with-fallback";

export interface InwardDeliveryFile {
  url: string;
  by?: string;
  at?: string;
  invoiceNumber?: string;
}

export interface InwardDeliveryFilesData {
  warehousePhotos?: InwardDeliveryFile[];
  financeDocuments?: InwardDeliveryFile[];
}

const FileGroup = ({
  title,
  files,
  testId,
  onOpen,
}: {
  title: string;
  files: InwardDeliveryFile[];
  testId: string;
  onOpen: (index: number) => void;
}) => (
  <div className="mb-5" data-testid={testId}>
    <h2 className="fs-18 fw-bolder mb-3">{title}</h2>
    {files.length === 0 ? (
      <div className="fs-15 text-muted">Not uploaded yet.</div>
    ) : (
      <div className="d-flex flex-wrap gap-3">
        {files.map((f, i) => (
          <button
            key={`${f.url}-${i}`}
            type="button"
            className="inward-doc-btn flex-column align-items-start"
            onClick={() => onOpen(i)}
            data-testid={`${testId}-file`}
          >
            {fileKind(f.url) === "image" ? (
              <ImageWithFallback
                src={f.url}
                alt={`${title} ${i + 1}`}
                className="inward-delivery-thumb"
                noDefaultSize
              />
            ) : (
              <span className="inward-viewer-file-ext fs-12 px-2 py-1">
                {fileExtension(f.url) || "FILE"}
              </span>
            )}
            <span className="fs-13 text-start">
              {f.by || "-"} · {formatDateTime(f.at)}
              {f.invoiceNumber ? ` · Invoice ${f.invoiceNumber}` : ""}
            </span>
          </button>
        ))}
      </div>
    )}
  </div>
);

const InwardDeliveryFiles = ({
  files,
}: {
  files?: InwardDeliveryFilesData | null;
}) => {
  const [viewer, setViewer] = useState<{
    urls: string[];
    index: number;
    title: string;
  } | null>(null);
  if (!files) return null;
  const photos = files.warehousePhotos ?? [];
  const docs = files.financeDocuments ?? [];
  return (
    <div className="mt-6" data-testid="inward-delivery-files">
      <FileGroup
        title="Warehouse verification photos"
        files={photos}
        testId="delivery-warehouse-photos"
        onOpen={(index) =>
          setViewer({
            urls: photos.map((f) => f.url),
            index,
            title: "Warehouse photo",
          })
        }
      />
      <FileGroup
        title="Finance documents"
        files={docs}
        testId="delivery-finance-documents"
        onOpen={(index) =>
          setViewer({
            urls: docs.map((f) => f.url),
            index,
            title: "Finance document",
          })
        }
      />
      <InwardImageViewer
        images={viewer?.urls ?? []}
        openIndex={viewer ? viewer.index : null}
        onClose={() => setViewer(null)}
        title={viewer?.title}
      />
    </div>
  );
};

export default InwardDeliveryFiles;
